import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'
import { ImagePlus, ImageUp, RefreshCw, Ticket, Trash2, X } from 'lucide-react'
import { Modal, useToast } from '../admin/primitives.js'
import type { VisuelSoiree } from '../finance/ClosingHero.js'
import { useConfirm } from '../shell/confirm.js'
import { envoyerAffiche, retirerAffiche, type ReponseAffiche, type Resultat } from './soirees-maison-api.js'
import { useMoi } from './soirees-maison-hooks.js'
import {
  GardeCles, proposeRecharger, raisonEcriture, stockageMemoire,
  type Affiche, type ErreurBM, type Soiree,
} from './soirees-maison-model.js'
import {
  cadreInitial, deplacerCadre, redimensionnerCadre, refusCadre, signatureEnvoi, tailleCadre, tailleSortie, urlAffiche,
  type AfficheBrouillon, type Cadre,
} from './affiche-model.js'
import { fichierAffiche, libererSource, lireSource, recadrer, type SourceAffiche } from './affiche-navigateur.js'
import type { Ecrivain } from './SoireeMaisonPage.js'

/**
 * L'AFFICHE D'UNE SOIRÉE — ses écrans (contrat `soirees-v1` §9.6).
 *
 *  · le BANDEAU en fondu de l'assistant, recette de la clôture de la compta
 *    (`BandeauSoiree`, classes `fwz-hero*`) : le fond flouté et voilé se fond
 *    vers le bas, l'affiche nette passe devant avec son rapport, la teinte
 *    dominante colore son liseré ; chaque image apparaît en fondu une fois
 *    chargée ;
 *  · sur la FICHE, le bandeau de la compta lui-même (`BandeauSoiree`), à qui
 *    l'écran donne l'affiche (`visuelAffiche`) ; sans affiche, ses halos
 *    d'ambiance et son icône — un repli, pas un manque ;
 *  · la VIGNETTE 16:9 des listes, chargée paresseusement ;
 *  · le RECADRAGE, grammaire `wz-drop` / `wz-crop` de l'assistant de fiche
 *    agent de la Main Courante : déposer, un cadre 16:9 à déplacer, un curseur
 *    pour sa taille, un aperçu ;
 *  · sur la fiche : « Ajouter / Changer l'affiche » (petite fenêtre, même
 *    recadrage) et « Retirer l'affiche » (confirmation).
 *
 * Les images viennent toujours de GESTION (`urlAffiche`) ou de l'appareil
 * (`blob:`, `data:`) : la CSP n'autorise rien d'autre.
 */

/* ═══════════════════════ les deux calques ═══════════════════════ */

/** Le fond flouté : posé en fondu quand il est chargé (« chargé » est mémorisé par adresse). */
export function FondAffiche({ src, onEchec }: { src: string; onEchec?: (() => void) | undefined }) {
  const [pret, setPret] = useState<string | null>(null)
  return (
    <div className={`bs-affiche-fond${pret === src ? ' bs-affiche-fond--pret' : ''}`} aria-hidden="true">
      <img src={src} alt="" decoding="async" onLoad={() => setPret(src)} onError={onEchec} />
    </div>
  )
}

/** L'affiche nette, à son rapport. */
export function NetAffiche(props: { src: string; alt: string; largeur: number; hauteur: number; className?: string | undefined; onEchec?: (() => void) | undefined }) {
  const [prete, setPrete] = useState<string | null>(null)
  return (
    <figure className={`bs-affiche-net${prete === props.src ? ' bs-affiche-net--prete' : ''}${props.className ? ` ${props.className}` : ''}`}
      style={{ aspectRatio: `${props.largeur} / ${props.hauteur}` }}>
      <img src={props.src} alt={props.alt} decoding="async" onLoad={() => setPrete(props.src)} onError={props.onEchec} />
    </figure>
  )
}

const avecTeinte = (teinte: string | null | undefined): CSSProperties | undefined =>
  teinte ? ({ ['--bs-teinte' as string]: teinte } as CSSProperties) : undefined

/* ═══════════════════════ la vignette des listes ═══════════════════════ */

/** Une vignette 16:9 ; sans affiche (ou si elle ne se charge pas), un repli neutre. Décorative : le nom est à côté. */
export function VignetteAffiche({ soireeId, affiche }: { soireeId: string; affiche: Affiche | null }) {
  const src = affiche ? urlAffiche(soireeId, 'net', affiche.empreinte) : null
  const [echec, setEchec] = useState<string | null>(null)
  const [chargee, setChargee] = useState<string | null>(null)
  if (!src || echec === src) {
    return <span className="bs-vignette bs-vignette--repli" aria-hidden="true"><Ticket size={16} /></span>
  }
  return (
    <span className={`bs-vignette${chargee === src ? ' bs-vignette--prete' : ''}`} style={avecTeinte(affiche?.teinte)} aria-hidden="true">
      <img src={src} alt="" loading="lazy" decoding="async" onLoad={() => setChargee(src)} onError={() => setEchec(src)} />
    </span>
  )
}

/* ═══════════════════════ le bandeau de la fiche ═══════════════════════ */

/**
 * L'affiche, dans la forme du visuel que lit le bandeau de la compta
 * (`BandeauSoiree`, ClosingHero.tsx) : la fiche d'une soirée porte le même
 * bandeau que la fiche d'une nuit. `null` sans affiche : le repli du bandeau.
 */
export function visuelAffiche(s: Soiree): VisuelSoiree | null {
  const a = s.affiche
  const net = a ? urlAffiche(s.id, 'net', a.empreinte) : null
  const fond = a ? urlAffiche(s.id, 'fond', a.empreinte) : null
  return a && net && fond ? { cover_url: net, backdrop_url: fond, width: a.largeur, height: a.hauteur, dominant_hex: a.teinte } : null
}

/* ═══════════════════════ le recadrage ═══════════════════════ */

/** Le pas du clavier sur le cadre : 2 % de l'image (10 % avec Maj). */
const PAS_CLAVIER = 0.02

/**
 * L'ÉTAPE DE L'AFFICHE — déposer, recadrer au 16:9, ou « Plus tard ».
 *
 * Le cadre est en pixels de l'image d'origine ; à l'écran il est posé en POUR
 * CENT de l'image, si bien qu'il suit la largeur de la fenêtre sans rien
 * remesurer. L'aperçu est l'image elle-même, recadrée par CSS : le `canvas`
 * ne sert qu'une fois, à « Continuer ».
 */
export function RecadrageAffiche(props: {
  source: SourceAffiche | null
  cadre: Cadre | null
  /** L'affiche déjà recadrée (brouillon relu, ou la précédente) quand l'image d'origine n'est plus là. */
  affiche: AfficheBrouillon | null
  onSource: (s: SourceAffiche) => void
  onCadre: (c: Cadre) => void
  onRetirer: () => void
  erreur: string | null
  onErreur: (m: string | null) => void
  /** L'assistant seulement : passer l'étape sans affiche. */
  plusTard?: (() => void) | undefined
  /** Le nom du geste de retrait (« Retirer l'image », « Ne pas changer »). */
  libelleRetirer?: string | undefined
}) {
  const entree = useRef<HTMLInputElement>(null)
  const [survol, setSurvol] = useState(false)
  const [lecture, setLecture] = useState(false)
  const choisir = () => entree.current?.click()
  // Une image lâchée à côté de la zone ne doit pas faire quitter Gestion (le navigateur l'ouvrirait à la place de la page).
  useEffect(() => {
    const garder = (e: DragEvent) => { if (e.dataTransfer?.types.includes('Files')) e.preventDefault() }
    document.addEventListener('dragover', garder)
    document.addEventListener('drop', garder)
    return () => { document.removeEventListener('dragover', garder); document.removeEventListener('drop', garder) }
  }, [])

  const lire = async (f: File | undefined) => {
    if (!f) return
    setLecture(true); props.onErreur(null)
    try {
      const r = await lireSource(f)
      if (r.ok) props.onSource(r.valeur)
      else props.onErreur(r.message)
    } finally { setLecture(false) }
  }

  const fichier = (
    <input ref={entree} type="file" accept="image/jpeg,image/png,image/webp" hidden
      onChange={(e) => { void lire(e.target.files?.[0]); e.target.value = '' }} />
  )
  const refus = props.erreur ? <p className="bsw-refus" role="alert">{props.erreur}</p> : null

  if (props.source && props.cadre) {
    return (
      <>
        {fichier}
        <Cadrage source={props.source} cadre={props.cadre} onCadre={props.onCadre} />
        <div className="bsw-crop__pied">
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={choisir} disabled={lecture}><ImageUp size={14} aria-hidden="true" /> Changer d’image</button>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={props.onRetirer}><X size={14} aria-hidden="true" /> {props.libelleRetirer ?? 'Retirer l’image'}</button>
        </div>
        {refus}
      </>
    )
  }

  if (props.affiche) {
    const a = props.affiche
    return (
      <>
        {fichier}
        <div className="bsw-pose">
          <NetAffiche className="bsw-pose__affiche" src={a.data_url} alt="L’affiche recadrée" largeur={a.largeur} hauteur={a.hauteur} />
          <div className="bsw-pose__cote">
            <span className="bsw-badge bsw-badge--ok">{a.largeur.toLocaleString('fr-FR')} × {a.hauteur.toLocaleString('fr-FR')}</span>
            <p className="bsw-note">L’affiche est prête. Elle partira juste après la création de la soirée, par un envoi à part.</p>
            <div className="bsw-crop__pied">
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={choisir} disabled={lecture}><ImageUp size={14} aria-hidden="true" /> Choisir une autre image</button>
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={props.onRetirer}><X size={14} aria-hidden="true" /> {props.libelleRetirer ?? 'Retirer l’affiche'}</button>
            </div>
          </div>
        </div>
        {refus}
      </>
    )
  }

  return (
    <>
      {fichier}
      <button type="button" className={`bsw-drop${survol ? ' bsw-drop--survol' : ''}`} onClick={choisir} disabled={lecture} aria-busy={lecture || undefined}
        onDragOver={(e) => { e.preventDefault(); setSurvol(true) }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSurvol(false) }}
        onDrop={(e) => { e.preventDefault(); setSurvol(false); void lire(e.dataTransfer.files[0]) }}>
        <ImagePlus size={34} aria-hidden="true" />
        <strong>{lecture ? 'Lecture de l’image…' : 'Déposer l’affiche ici'}</strong>
        <span>ou cliquez pour la choisir — JPEG, PNG ou WebP. Elle sera recadrée au format paysage de Shotgun, 640 px de large au moins.</span>
      </button>
      {refus}
      {props.plusTard ? (
        <div className="bsw-plus-tard">
          <button type="button" className="bsw-lien" onClick={props.plusTard}>Plus tard</button>
          <span className="bsw-note">Vous pourrez l’ajouter depuis la fiche de la soirée.</span>
        </div>
      ) : null}
    </>
  )
}

/** Le cadre à déplacer (glisser, ou les flèches du clavier) et à agrandir (curseur, ou + et −). */
function Cadrage({ source: s, cadre: c, onCadre }: { source: SourceAffiche; cadre: Cadre; onCadre: (c: Cadre) => void }) {
  const vue = useRef<HTMLDivElement>(null)
  const prise = useRef<{ px: number; py: number; depart: Cadre; k: number } | null>(null)
  const [saisi, setSaisi] = useState(false)
  const sortie = tailleSortie(c)
  const refus = refusCadre(c)
  const taille = tailleCadre(c, s.iw, s.ih)

  const echelle = () => (vue.current ? vue.current.getBoundingClientRect().width / s.iw : 1)
  const saisir = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    const k = echelle()
    let depart = c
    // Un appui hors du cadre l'y amène : on vise, puis on glisse.
    if (!(e.target as HTMLElement).closest('.bsw-crop__cadre') && vue.current) {
      const r = vue.current.getBoundingClientRect()
      depart = deplacerCadre(c, (e.clientX - r.left) / k - (c.x + c.w / 2), (e.clientY - r.top) / k - (c.y + c.h / 2), s.iw, s.ih)
      onCadre(depart)
    }
    prise.current = { px: e.clientX, py: e.clientY, depart, k }
    e.currentTarget.setPointerCapture(e.pointerId)
    setSaisi(true)
  }
  const glisser = (e: PointerEvent<HTMLDivElement>) => {
    const p = prise.current
    if (!p) return
    onCadre(deplacerCadre(p.depart, (e.clientX - p.px) / p.k, (e.clientY - p.py) / p.k, s.iw, s.ih))
  }
  const lacher = () => { prise.current = null; setSaisi(false) }
  const clavier = (e: KeyboardEvent<HTMLDivElement>) => {
    const pas = s.iw * PAS_CLAVIER * (e.shiftKey ? 5 : 1)
    const d: Record<string, [number, number]> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] }
    if (d[e.key]) { e.preventDefault(); onCadre(deplacerCadre(c, d[e.key]![0], d[e.key]![1], s.iw, s.ih)); return }
    if (e.key === '+' || e.key === '=') { e.preventDefault(); onCadre(redimensionnerCadre(c, taille + 5, s.iw, s.ih)) }
    if (e.key === '-') { e.preventDefault(); onCadre(redimensionnerCadre(c, taille - 5, s.iw, s.ih)) }
  }
  const pc = (v: number, total: number) => `${(v / total) * 100}%`

  return (
    <div className="bsw-crop">
      <div className="bsw-crop__scene">
        <div ref={vue} className="bsw-crop__vue" style={{ aspectRatio: `${s.iw} / ${s.ih}`, ['--bsw-ratio' as string]: String(s.iw / s.ih) } as CSSProperties}
          onPointerDown={saisir} onPointerMove={glisser} onPointerUp={lacher} onPointerCancel={lacher}>
          <img src={s.url} alt="" draggable={false} />
          <div className={`bsw-crop__cadre${saisi ? ' bsw-crop__cadre--saisi' : ''}${refus ? ' bsw-crop__cadre--refus' : ''}`}
            style={{ left: pc(c.x, s.iw), top: pc(c.y, s.ih), width: pc(c.w, s.iw), height: pc(c.h, s.ih) }}
            tabIndex={0} aria-label="Cadre de l’affiche : glissez-le, ou déplacez-le avec les flèches ; + et − changent sa taille." onKeyDown={clavier}>
            <span /><span /><span /><span />
          </div>
        </div>
      </div>
      <div className="bsw-crop__cote">
        <span className={`bsw-badge${refus ? '' : ' bsw-badge--ok'}`}>{refus ? 'Zone trop étroite' : `${sortie.largeur.toLocaleString('fr-FR')} × ${sortie.hauteur.toLocaleString('fr-FR')}`}</span>
        <p className="bsw-note">{refus ?? 'Déplacez le cadre sur ce qui doit rester ; ce qui dépasse est coupé. Le curseur l’agrandit ou le réduit.'}</p>
        <label className="lc-field bsw-crop__taille">
          <span className="lc-label">Taille du cadre</span>
          <input type="range" min={20} max={100} value={taille} aria-valuetext={`${taille} %`}
            onChange={(e) => onCadre(redimensionnerCadre(c, Number(e.target.value), s.iw, s.ih))} />
        </label>
        <div className="bsw-crop__apercu">
          <span className="bsw-crop__apercu-cadre" aria-hidden="true">
            <img src={s.url} alt="" draggable={false} style={{ width: pc(s.iw, c.w), left: `${(-c.x / c.w) * 100}%`, top: `${(-c.y / c.h) * 100}%` }} />
          </span>
          <span className="bsw-crop__apercu-txt">Aperçu</span>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════ l'envoi ═══════════════════════ */

const AFFICHE_ILLISIBLE: ErreurBM = {
  genre: 'autre', code: 'AFFICHE_ILLISIBLE', status: 0,
  message: 'L’affiche gardée ne se relit pas : ajoutez-la depuis la fiche de la soirée.',
  champs: [], version: null, ambiguites: [], traceId: null,
}

/**
 * Envoie une affiche recadrée (§9.5) avec SA clé d'idempotence, rangée par
 * soirée : la même image renvoyée reprend la même clé (« Réessayer » ne pose
 * rien deux fois), une autre en tire une neuve. Jamais la clé de la création.
 */
export async function envoyerAfficheRecadree(soireeId: string, a: AfficheBrouillon, versionAffiche: number | null, cles: GardeCles): Promise<Resultat<ReponseAffiche>> {
  const fichier = fichierAffiche(a)
  if (!fichier) return { ok: false, erreur: AFFICHE_ILLISIBLE }
  const ecran = `affiche:${soireeId}`
  const r = await envoyerAffiche(soireeId, fichier, versionAffiche, cles.pour(ecran, signatureEnvoi(versionAffiche, a.data_url)))
  if (r.ok) cles.oublier(ecran)
  return r
}

/* ═══════════════════════ sur la fiche ═══════════════════════ */

export const PHRASE_ENTRAINEMENT = 'Soirée d’entraînement : elle se gère depuis la page de secours.'

/**
 * Les gestes de l'affiche sur la fiche : seulement avec le droit de préparer
 * (comme les autres gestes de préparation) ; grisés, avec leur raison, quand
 * l'écriture n'est pas possible (gel, Mode sûr, raccordement) ou que la
 * soirée est d'entraînement. L'affiche se change quel que soit l'état de la
 * soirée, en vente comprise (§9.4) : elle ne change rien à la vente.
 */
export function GestesAffiche({ ecrivain }: { ecrivain: Ecrivain }) {
  const { soiree: s, fiche } = ecrivain
  const confirm = useConfirm()
  const toast = useToast()
  const moi = useMoi()
  const cles = useMemo(() => new GardeCles(stockageMemoire(), moi ?? 'inconnu'), [moi])
  const [ouverte, setOuverte] = useState(false)
  const [tour, setTour] = useState(0)
  const [envoi, setEnvoi] = useState(false)
  if (!fiche.actions.preparer) return null
  const raison = raisonEcriture(fiche.ecriture) ?? (s.entrainement ? PHRASE_ENTRAINEMENT : null)
  const a = s.affiche

  const retirer = async () => {
    if (!a) return
    if (!(await confirm({
      title: `Retirer l’affiche de « ${s.nom} » ?`,
      message: 'La soirée n’aura plus d’affiche : ni sur sa fiche, ni dans les listes. Vous pourrez en poser une autre quand vous voudrez.',
      variant: 'sensitive', confirmLabel: 'Retirer l’affiche',
    }))) return
    setEnvoi(true)
    try {
      const corps = { version_affiche: a.version }
      const ecran = `affiche-retrait:${s.id}`
      const r = await retirerAffiche(s.id, a.version, cles.pour(ecran, JSON.stringify(corps)))
      if (r.ok) {
        cles.oublier(ecran)
        ecrivain.signalerErreur(null)
        toast('ok', r.data.rejouee ? 'Déjà fait : la billetterie avait déjà retiré cette affiche.' : r.data.inchange ? 'Cette soirée n’avait déjà plus d’affiche.' : 'Affiche retirée.')
        ecrivain.recharger()
      } else ecrivain.signalerErreur(r.erreur)
    } finally { setEnvoi(false) }
  }

  return (
    <div className="bs-affiche-gestes">
      <span className="bs-affiche-gestes__boutons">
        <button type="button" className="lc-btn lc-btn--sm" disabled={!!raison || envoi} onClick={() => { setTour((n) => n + 1); setOuverte(true) }}>
          {a ? <><ImageUp size={14} aria-hidden="true" /> Changer l’affiche</> : <><ImagePlus size={14} aria-hidden="true" /> Ajouter une affiche</>}
        </button>
        {a ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm lc-btn--danger-text" disabled={!!raison || envoi} onClick={() => { void retirer() }}>
            <Trash2 size={14} aria-hidden="true" /> Retirer l’affiche
          </button>
        ) : null}
      </span>
      {raison ? <span className="lc-hint bs-geste__raison">{raison}</span> : null}
      <FenetreAffiche key={tour} ouverte={ouverte} onFermer={() => setOuverte(false)} soiree={s} cles={cles} onRecharger={ecrivain.recharger}
        onPosee={(r) => {
          ecrivain.signalerErreur(null)
          toast('ok', r.rejouee ? 'Déjà fait : la billetterie avait déjà posé cette affiche.' : 'Affiche posée : elle apparaît sur la fiche et dans les listes.')
          ecrivain.recharger()
        }} />
    </div>
  )
}

/**
 * « Ajouter / Changer l'affiche » : une petite fenêtre, le même recadrage que
 * l'assistant. L'envoi porte la version LUE (`aucune` sans affiche) : une
 * affiche changée entre-temps par quelqu'un d'autre est refusée, et la fenêtre
 * propose de recharger.
 */
function FenetreAffiche(props: {
  ouverte: boolean; onFermer: () => void; soiree: Soiree; cles: GardeCles
  onPosee: (r: ReponseAffiche) => void
  /** Après un refus « version périmée » : relire la fiche, qui porte l'affiche posée entre-temps. */
  onRecharger: () => void
}) {
  const [source, setSource] = useState<SourceAffiche | null>(null)
  const [cadre, setCadre] = useState<Cadre | null>(null)
  const [refus, setRefus] = useState<string | null>(null)
  const [erreur, setErreur] = useState<ErreurBM | null>(null)
  const [envoi, setEnvoi] = useState(false)
  // Lu par la pile des surfaces (Échap), qui garde la fonction de l'ouverture : un état y serait figé.
  const enCours = useRef(false)
  const sourceLue = useRef<SourceAffiche | null>(null)
  sourceLue.current = source
  useEffect(() => () => libererSource(sourceLue.current), [])
  const s = props.soiree
  const fermer = () => { if (!enCours.current) props.onFermer() }

  const poser = async () => {
    if (!source || !cadre || enCours.current) return
    enCours.current = true
    setEnvoi(true); setErreur(null)
    try {
      const r = await recadrer(source, cadre)
      if (!r.ok) { setRefus(r.message); return }
      const e = await envoyerAfficheRecadree(s.id, r.valeur, s.affiche?.version ?? null, props.cles)
      if (e.ok) { enCours.current = false; props.onFermer(); props.onPosee(e.data) } else setErreur(e.erreur)
    } finally { enCours.current = false; setEnvoi(false) }
  }

  return (
    <Modal open={props.ouverte} onClose={fermer} owning ariaLabel={s.affiche ? 'Changer l’affiche' : 'Ajouter une affiche'} className="bsw bsw--petite">
      <div className="bsw-fenetre">
        <header className="bsw-tete">
          <div className="bsw-tete__haut">
            <div className="bsw-tete__textes">
              <p className="bsw-surtitre">Affiche · {s.nom}</p>
              <h2 className="bsw-titre">{s.affiche ? 'Quelle nouvelle affiche ?' : 'Quelle est l’affiche de la soirée ?'}</h2>
              <p className="bsw-sous">Au format paysage de Shotgun (16:9). {s.affiche ? 'Elle remplace l’actuelle partout.' : 'Elle apparaîtra sur la fiche et dans les listes.'}</p>
            </div>
            <button type="button" className="bsw-fermer" aria-label="Fermer" onClick={fermer} disabled={envoi}><X size={16} aria-hidden="true" /></button>
          </div>
        </header>
        <div className="bsw-corps">
          <div className="bsw-etape">
            <RecadrageAffiche source={source} cadre={cadre} affiche={null}
              onSource={(x) => { libererSource(source); setSource(x); setCadre(cadreInitial(x.iw, x.ih)); setRefus(null) }}
              onCadre={(c) => { setCadre(c); setRefus(null) }}
              onRetirer={() => { libererSource(source); setSource(null); setCadre(null); setRefus(null) }}
              erreur={refus} onErreur={setRefus} />
            {erreur ? (
              <div className="bs-note bs-note--ko" role="alert">
                <span>L’affiche n’a pas été posée : {erreur.message}</span>
                {proposeRecharger(erreur) ? <button type="button" className="lc-btn lc-btn--sm" onClick={() => { props.onFermer(); props.onRecharger() }}><RefreshCw size={14} aria-hidden="true" /> Recharger la soirée</button> : null}
              </div>
            ) : null}
          </div>
        </div>
        <footer className="bsw-pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer} disabled={envoi}>Annuler</button>
          <span className="bsw-compteur" />
          <button type="button" className="lc-btn lc-btn--primary" disabled={!source || !cadre || envoi} onClick={() => { void poser() }}>
            {envoi ? 'Envoi…' : 'Poser l’affiche'}
          </button>
        </footer>
      </div>
    </Modal>
  )
}
